import { useQueries, useQuery } from '../use-query';
import { salesFeeService } from '../../../services';
import { showToRoles } from '../../../utils';
import { FeeType, SortDirection, User } from '../../../types';
import { SalesFeeStatusToLabels } from '../../../const';

export const useSalesFeesQuery = (keys: any) => {
  return useQuery(
    ['sales-fees', JSON.stringify(keys)],
    () => {
      return salesFeeService
        .getAll({
          params: keys,
        })
        .then((res) => res.data)
        .then((res) => {
          const data = res.data.map((data) => {
            const { salesFee } = data;
            if (!salesFee) {
              return {
                ...data,
                salesFee: {
                  id: '',
                  feeStatus: 'MISSING' as keyof typeof SalesFeeStatusToLabels,
                  activationDate: null,
                },
              };
            }

            const i = salesFee.feeRecords.findIndex(
              (feeRecords) => feeRecords.id === salesFee.activeFeeRecordId,
            );

            salesFee.activationDate = salesFee.feeRecords[i].activationDate;
            salesFee.feeStatus = 'ACTIVE';

            return { ...data, salesFee };
          });

          return { ...res, data };
        });
    },
    { initialData: { data: [], totalCount: 0 }, keepPreviousData: true },
  );
};

export const useSalesFeeQuery = (user: User, id = '', sortDirection?: SortDirection) => {
  return useQuery(
    ['sales-fee', id],
    () => {
      return salesFeeService
        .get(id)
        .then((res) => res.data.data)
        .then((res) => {
          if (sortDirection) {
            const isAccessToChange = showToRoles(
              ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'],
              user,
            );
            let isActive = true;
            res.feeRecords = res.feeRecords.sort((a, b) => {
              if (sortDirection === 'asc')
                return new Date(a.activationDate).getTime() - new Date(b.activationDate).getTime();

              return new Date(b.activationDate).getTime() - new Date(a.activationDate).getTime();
            });

            res.feeRecords = res.feeRecords.map((record) => {
              if (isActive) {
                isActive = res.activeFeeRecordId !== record.id;
              }

              return {
                ...record,
                isActive: isAccessToChange && (isActive || res.activeFeeRecordId === record.id),
              };
            });
          }

          return res;
        });
    },
    {
      initialData: { data: {} },
    },
  );
};

export const initialDataSalesFeeActive = {
  '20DC': 0,
  '20HC': 0,
  '20RF': 0,
  '40DC': 0,
  '40HC': 0,
  '40RF': 0,
  '40OT': 0,
  '40FR': 0,
  '45HC': 0,
  feeType: FeeType.FIXED,
};

export const useSalesFeeActiveQuery = ({ yardId = '' }) => {
  const query = useQuery(
    ['sales-fee', 'get-active-fee', yardId],
    () => salesFeeService.getActiveFee(yardId),
    { initialData: initialDataSalesFeeActive, keepPreviousData: true, enabled: !!yardId },
  );

  return { ...query, data: query.data || initialDataSalesFeeActive };
};

export const useMultipleSalesFeeQueries = (yards: { yardId: string }[]) => {
  const queries = useQueries(
    yards.map((yard) => ({
      key: ['sales-fee', 'get-active-fee', yard.yardId],
      queryFn: () => salesFeeService.getActiveFee(yard.yardId),
      initialData: initialDataSalesFeeActive,
      keepPreviousData: true,
      enabled: !!yard.yardId,
    })),
  );

  return queries.map((query) => ({ ...query, data: query.data || initialDataSalesFeeActive }));
};
